/**
 * batch-47 gespreksverslagen (spec 90) - event-gedreven statusverloop.
 *
 * Ontwerpprincipe (§2): events zijn de waarheid, statussen zijn AFGELEID. Dit
 * bestand levert de gedeelde enums, labels, DTO's en Zod-schemas; de afleiding
 * zelf gebeurt in EEN pure resolver (`bepaalGespreksverslagStatus` in
 * @streep/utils) die de statusbalk, de tijdlijn, de filters, de tellers en de
 * dashboard-widget voedt.
 *
 * Privacy: een event-payload bevat NOOIT gespreksinhoud - de tijdlijn is
 * breder zichtbaar dan het verslag zelf (verzuim-redactie). Het Geopend-event
 * registreert bewust geen apparaat-/omgevingsdata (AVG-minimalisatie, A2).
 */

import { z } from 'zod'

// ─── Enums (matching Prisma) ─────────────────────────────────────────────────

export const GESPREKSVERSLAG_EVENT_SOORT = [
  'AANGEMAAKT',
  'GEWIJZIGD',
  'TER_CONTROLE_AANGEBODEN',
  'TER_CONTROLE_GOEDGEKEURD',
  'TER_CONTROLE_TERUGGESTUURD',
  'DEFINITIEF',
  'ONDERTEKEND_STREEP',
  'VERZONDEN',
  'GEOPEND',
  'GELEZEN',
  'GEDOWNLOAD',
  'GETEKEND',
  'ONDERTEKENING_GEWEIGERD',
  'ONDERTEKENING_ADMINISTRATIEF_AFGESLOTEN',
  'STILZWIJGEND_AKKOORD',
  'REACTIE',
  'HERINNERD',
  'VERLOPEN',
  'TERMIJN_GEPAUZEERD',
  'TERMIJN_HERVAT',
  'TERMIJN_VERLENGD',
  'VERSIE_GEMAAKT',
  'HEROPEND',
  'ADDENDUM_TOEGEVOEGD',
  'DEELNEMER_GEWIJZIGD',
  'BIJLAGE_GEWIJZIGD',
  'ONDERTEKENAARS_VASTGESTELD',
  'VERVOLGAFSPRAAK_INGEPLAND',
  'ACTIEPUNT_AFGEROND',
  'ACTIEPUNT_HEROPEND',
  'GEARCHIVEERD',
] as const
export type GespreksverslagEventSoort = (typeof GESPREKSVERSLAG_EVENT_SOORT)[number]

export const GESPREKSVERSLAG_HOOFDSTATUS = [
  'CONCEPT',
  'TER_CONTROLE',
  'DEFINITIEF',
  'VERZONDEN',
  'GEOPEND',
  'GELEZEN',
  'WACHT_OP_ONDERTEKENING',
  'GEDEELTELIJK_ONDERTEKEND',
  'VOLLEDIG_ONDERTEKEND',
  'ONDERTEKENING_GEWEIGERD',
  'VERLOPEN',
  'GEARCHIVEERD',
] as const
export type GespreksverslagHoofdstatus = (typeof GESPREKSVERSLAG_HOOFDSTATUS)[number]

export const GESPREKSVERSLAG_HOOFDSTATUS_LABELS: Record<GespreksverslagHoofdstatus, string> = {
  CONCEPT:                  'Concept',
  TER_CONTROLE:             'Ter controle',
  DEFINITIEF:               'Definitief',
  VERZONDEN:                'Verzonden',
  GEOPEND:                  'Geopend',
  GELEZEN:                  'Gelezen',
  WACHT_OP_ONDERTEKENING:   'Wacht op ondertekening',
  GEDEELTELIJK_ONDERTEKEND: 'Gedeeltelijk ondertekend',
  VOLLEDIG_ONDERTEKEND:     'Volledig ondertekend',
  ONDERTEKENING_GEWEIGERD:  'Ondertekening geweigerd',
  VERLOPEN:                 'Verlopen',
  GEARCHIVEERD:             'Gearchiveerd',
}

/**
 * Vaste kleurcode van de statuslabels (PO §7): grijs = nog niets gebeurd,
 * blauw = onderweg, oranje = wacht op iemand, rood = probleem, groen = klaar.
 * Eén bron voor widget, overzicht en detail.
 */
export type GespreksverslagStatusKleur = 'grijs' | 'blauw' | 'oranje' | 'rood' | 'groen'

export const GESPREKSVERSLAG_HOOFDSTATUS_KLEUR: Record<
  GespreksverslagHoofdstatus,
  GespreksverslagStatusKleur
> = {
  CONCEPT:                  'grijs',
  TER_CONTROLE:             'blauw',
  DEFINITIEF:               'blauw',
  VERZONDEN:                'blauw',
  GEOPEND:                  'blauw',
  GELEZEN:                  'groen',
  WACHT_OP_ONDERTEKENING:   'oranje',
  GEDEELTELIJK_ONDERTEKEND: 'oranje',
  VOLLEDIG_ONDERTEKEND:     'groen',
  ONDERTEKENING_GEWEIGERD:  'rood',
  VERLOPEN:                 'rood',
  GEARCHIVEERD:             'groen',
}

export const GESPREKSVERSLAG_ACTIE_STATUS = [
  'GEEN',
  'ACTIE_MEDEWERKER',
  'ACTIE_LEIDINGGEVENDE',
  'ACTIE_HR',
  'TERMIJN_OVERSCHREDEN',
  'OPEN_ACTIEPUNTEN',
  'VERVOLG_TE_PLANNEN',
] as const
export type GespreksverslagActieStatus = (typeof GESPREKSVERSLAG_ACTIE_STATUS)[number]

export const GESPREKSVERSLAG_ACTIE_STATUS_LABELS: Record<GespreksverslagActieStatus, string> = {
  GEEN:                 'Geen openstaande acties',
  ACTIE_MEDEWERKER:     'Actie vereist van medewerker',
  ACTIE_LEIDINGGEVENDE: 'Actie vereist van leidinggevende',
  ACTIE_HR:             'Actie vereist van HR',
  TERMIJN_OVERSCHREDEN: 'Termijn overschreden',
  OPEN_ACTIEPUNTEN:     'Actiepunten open',
  VERVOLG_TE_PLANNEN:   'Vervolgafspraak nog te plannen',
}

export const GESPREKSVERSLAG_ACTIE_STATUS_KLEUR: Record<
  GespreksverslagActieStatus,
  GespreksverslagStatusKleur
> = {
  GEEN:                 'groen',
  ACTIE_MEDEWERKER:     'oranje',
  ACTIE_LEIDINGGEVENDE: 'oranje',
  ACTIE_HR:             'oranje',
  TERMIJN_OVERSCHREDEN: 'rood',
  OPEN_ACTIEPUNTEN:     'blauw',
  VERVOLG_TE_PLANNEN:   'blauw',
}

export const GESPREKSVERSLAG_VERSTREK_KANAAL = [
  'PORTAL',
  'EMAIL',
  'FYSIEK',
  'AANGETEKEND',
  'MONDELING',
] as const
export type GespreksverslagVerstrekKanaal = (typeof GESPREKSVERSLAG_VERSTREK_KANAAL)[number]

export const GESPREKSVERSLAG_VERSTREK_KANAAL_LABELS: Record<
  GespreksverslagVerstrekKanaal,
  string
> = {
  PORTAL:      'Portal (app)',
  EMAIL:       'E-mail',
  FYSIEK:      'Fysiek overhandigd',
  AANGETEKEND: 'Aangetekend verstuurd',
  MONDELING:   'Mondeling toegelicht',
}

/** Kanalen die als "alternatieve verstrekking" gelden (§12: bewijs verplicht-baar). */
export const GESPREKSVERSLAG_ALTERNATIEVE_KANALEN: readonly GespreksverslagVerstrekKanaal[] = [
  'FYSIEK',
  'AANGETEKEND',
  'MONDELING',
]

export const GESPREKSVERSLAG_TER_CONTROLE_MODUS = ['UIT', 'OPTIONEEL', 'VERPLICHT'] as const
export type GespreksverslagTerControleModus =
  (typeof GESPREKSVERSLAG_TER_CONTROLE_MODUS)[number]

export const GESPREKSVERSLAG_TER_CONTROLE_MODUS_LABELS: Record<
  GespreksverslagTerControleModus,
  string
> = {
  UIT:       'Geen controlestap',
  OPTIONEEL: 'Optioneel ter controle',
  VERPLICHT: 'Verplicht ter controle',
}

export const GESPREKSVERSLAG_ONDERTEKEN_PLICHT = [
  'GEEN',
  'MEDEWERKER',
  'MEDEWERKER_LEIDINGGEVENDE',
  'MEDEWERKER_LEIDINGGEVENDE_HR',
  'HANDMATIG',
] as const
export type GespreksverslagOndertekenPlicht =
  (typeof GESPREKSVERSLAG_ONDERTEKEN_PLICHT)[number]

export const GESPREKSVERSLAG_ONDERTEKEN_PLICHT_LABELS: Record<
  GespreksverslagOndertekenPlicht,
  string
> = {
  GEEN:                         'Geen ondertekening (stilzwijgend akkoord)',
  MEDEWERKER:                   'Alleen de medewerker',
  MEDEWERKER_LEIDINGGEVENDE:    'Medewerker en leidinggevende',
  MEDEWERKER_LEIDINGGEVENDE_HR: 'Medewerker, leidinggevende en HR',
  HANDMATIG:                    'Handmatig gekozen per verslag',
}

export const GESPREKSVERSLAG_TEKENVERKLARING = [
  'GELEZEN_EN_AKKOORD',
  'GELEZEN_NIET_AKKOORD',
  'ALLEEN_ONTVANGST',
  'MET_OPMERKING',
] as const
export type GespreksverslagTekenverklaring = (typeof GESPREKSVERSLAG_TEKENVERKLARING)[number]

export const GESPREKSVERSLAG_TEKENVERKLARING_LABELS: Record<
  GespreksverslagTekenverklaring,
  string
> = {
  GELEZEN_EN_AKKOORD:   'Gelezen en akkoord',
  GELEZEN_NIET_AKKOORD: 'Gelezen, niet akkoord',
  ALLEEN_ONTVANGST:     'Uitsluitend voor ontvangst',
  MET_OPMERKING:        'Akkoord met opmerking',
}

export const GESPREKSVERSLAG_ONDERTEKENAAR_ROL = ['MEDEWERKER', 'LEIDINGGEVENDE', 'HR'] as const
export type GespreksverslagOndertekenaarRol =
  (typeof GESPREKSVERSLAG_ONDERTEKENAAR_ROL)[number]

export const GESPREKSVERSLAG_ONDERTEKENAAR_ROL_LABELS: Record<
  GespreksverslagOndertekenaarRol,
  string
> = {
  MEDEWERKER:    'Medewerker',
  LEIDINGGEVENDE: 'Leidinggevende',
  HR:            'HR',
}

export const GESPREKSVERSLAG_ACTIEPUNT_PRIORITEIT = ['LAAG', 'NORMAAL', 'HOOG'] as const
export type GespreksverslagActiepuntPrioriteit =
  (typeof GESPREKSVERSLAG_ACTIEPUNT_PRIORITEIT)[number]

export const GESPREKSVERSLAG_ACTIEPUNT_PRIORITEIT_LABELS: Record<
  GespreksverslagActiepuntPrioriteit,
  string
> = {
  LAAG:    'Laag',
  NORMAAL: 'Normaal',
  HOOG:    'Hoog',
}

// ─── Statusbalk (§4) ─────────────────────────────────────────────────────────

export const GESPREKSVERSLAG_STAP_SLEUTELS = [
  'CONCEPT',
  'TER_CONTROLE',
  'DEFINITIEF',
  'VERZONDEN',
  'GELEZEN',
  'ONDERTEKEND',
  'GEARCHIVEERD',
] as const
export type GespreksverslagStapSleutel = (typeof GESPREKSVERSLAG_STAP_SLEUTELS)[number]

export const GESPREKSVERSLAG_STAP_LABELS: Record<GespreksverslagStapSleutel, string> = {
  CONCEPT:      'Concept',
  TER_CONTROLE: 'Ter controle',
  DEFINITIEF:   'Definitief',
  VERZONDEN:    'Verzonden',
  GELEZEN:      'Gelezen',
  ONDERTEKEND:  'Ondertekend',
  GEARCHIVEERD: 'Gearchiveerd',
}

export type GespreksverslagStapStaat = 'VOLTOOID' | 'HUIDIG' | 'TOEKOMSTIG' | 'OVERGESLAGEN'

/** Eén stap van de statusbalk: voltooid gemarkeerd, huidige benadrukt, rest licht. */
export interface GespreksverslagStatusStapDTO {
  sleutel: GespreksverslagStapSleutel
  label: string
  staat: GespreksverslagStapStaat
  /** ISO-moment waarop de stap bereikt is (null als nog niet). */
  op: string | null
  /** Wie de stap zette (naam) - hover/uitklap in de balk. */
  actorNaam: string | null
  /** Opmerking/reden bij de stap (weiger-reden, versiereden, pauze-reden). */
  toelichting: string | null
}

// ─── Tijdlijn-DTO ────────────────────────────────────────────────────────────

/**
 * Procesmetadata van een event. Bewust een smalle, primitieve shape: hier past
 * geen gespreksinhoud in (privacy §13/CC).
 */
export type GespreksverslagEventMeta = Record<string, string | number | boolean | null>

export interface GespreksverslagEventDTO {
  id: string
  soort: GespreksverslagEventSoort
  /** ISO-instant. */
  moment: string
  versieNr: number | null
  /** Naam van de actor (user of medewerker), of null bij systeem/onbekend. */
  actorNaam: string | null
  systeem: boolean
  kanaal: GespreksverslagVerstrekKanaal | null
  reden: string | null
  meta: GespreksverslagEventMeta | null
  /** Kant-en-klare NL-zin voor de tijdlijn (gedeeld gebouwd, nooit inhoud). */
  omschrijving: string
}

// ─── Ondertekenaars (§6) ─────────────────────────────────────────────────────

export interface GespreksverslagOndertekenaarDTO {
  id: string
  versieNr: number
  rol: GespreksverslagOndertekenaarRol
  employeeId: string
  employeeNaam: string | null
  volgorde: number
  /** ISO of null - handtekening op DEZE versie (DocumentSignature). */
  getekendOp: string | null
  /** ISO of null - uit een ONDERTEKENING_GEWEIGERD-event. */
  geweigerdOp: string | null
  weigerReden: string | null
  /** §12 - HR sluit een openstaande handtekening administratief af (uit dienst). */
  administratiefAfgeslotenOp: string | null
  administratiefAfgeslotenReden: string | null
}

// ─── Termijnstand (§7) ───────────────────────────────────────────────────────

export interface GespreksverslagTermijnConfig {
  herinnering1Dagen: number
  herinnering2Dagen: number
  escalatieTlDagen: number
  escalatieHrDagen: number
  verlopenDagen: number
}

export const GESPREKSVERSLAG_TERMIJN_DEFAULTS: GespreksverslagTermijnConfig = {
  herinnering1Dagen: 2,
  herinnering2Dagen: 5,
  escalatieTlDagen: 7,
  escalatieHrDagen: 10,
  verlopenDagen: 14,
}

export interface GespreksverslagTermijnStand {
  /** Loopt de klok (verzonden en niet afgerond)? */
  gestart: boolean
  gepauzeerd: boolean
  pauzeReden: string | null
  /** Effectief verstreken dagen (pauzes eraf). */
  dagenOpen: number
  /** Effectieve uiterste datum (ISO), null als niet gestart. */
  verlooptOp: string | null
  verlopen: boolean
  /** Opgetelde pauze-dagen. */
  pauzeDagen: number
  /** Opgetelde handmatige verlenging in dagen. */
  verlengdMetDagen: number
}

// ─── Zod: mutaties ───────────────────────────────────────────────────────────

/** §3/§12 - verzenden met kanaal-registratie (+ bewijs bij alternatieve verstrekking). */
export const gespreksverslagVerzendSchema = z
  .object({
    kanaal: z.enum(GESPREKSVERSLAG_VERSTREK_KANAAL).default('PORTAL'),
    /** Toelichting bij een alternatieve verstrekking (adres, ontvanger, wijze). */
    toelichting: z.string().trim().max(1000).optional(),
    /** Document-id van het geuploade bewijsstuk (verzendbewijs/ontvangstbevestiging). */
    bewijsDocumentId: z.string().trim().min(1).optional(),
    /**
     * §6 - extra verplichte ondertekenaars die de route niet zelf kan afleiden
     * (HR, of alle rollen bij ondertekenplicht HANDMATIG). De medewerker en de
     * gespreksvoerder worden automatisch ingevuld.
     */
    ondertekenaars: z
      .array(
        z.object({
          employeeId: z.string().trim().min(1, 'Kies een medewerker'),
          rol: z.enum(GESPREKSVERSLAG_ONDERTEKENAAR_ROL),
        }),
      )
      .max(10, 'Maximaal 10 ondertekenaars')
      .optional(),
  })
  .superRefine((v, ctx) => {
    if (
      GESPREKSVERSLAG_ALTERNATIEVE_KANALEN.includes(v.kanaal) &&
      !v.toelichting &&
      !v.bewijsDocumentId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['toelichting'],
        message: 'Leg vast hoe het verslag is verstrekt (toelichting of bewijsstuk)',
      })
    }
  })
export type GespreksverslagVerzendInput = z.infer<typeof gespreksverslagVerzendSchema>

/** §3/T2 - ondertekening weigeren mét verplichte reden. */
export const gespreksverslagWeigerSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(5, 'Geef aan waarom je niet tekent (min. 5 tekens)')
    .max(2000),
})
export type GespreksverslagWeigerInput = z.infer<typeof gespreksverslagWeigerSchema>

/** FO-126-leesbevestiging op het verslag. Expliciet GEEN akkoord (§3/T3). */
export const gespreksverslagLeesbevestigingSchema = z.object({
  opmerking: z.string().trim().max(2000).optional(),
})
export type GespreksverslagLeesbevestigingInput = z.infer<
  typeof gespreksverslagLeesbevestigingSchema
>

/**
 * De tekst die bij de leesbevestiging staat. Hardgecodeerd gedeeld zodat portal
 * en backoffice exact dezelfde (niet-akkoord-impliceren de) formulering tonen.
 */
export const GESPREKSVERSLAG_LEESBEVESTIGING_COPY = {
  knop: 'Bevestig dat ik dit verslag heb gelezen',
  uitleg:
    'Met deze bevestiging leg je alleen vast dat je het verslag hebt gelezen. Dit is geen akkoord met de inhoud; akkoord geef je met een handtekening.',
  bevestigd: 'Je hebt bevestigd dat je dit verslag hebt gelezen (geen akkoord met de inhoud).',
} as const

/** §7 - termijn pauzeren/hervatten/verlengen. */
export const gespreksverslagTermijnPauzeSchema = z.object({
  reden: z
    .string()
    .trim()
    .min(5, 'Geef een reden voor het pauzeren (min. 5 tekens)')
    .max(1000),
})
export type GespreksverslagTermijnPauzeInput = z.infer<
  typeof gespreksverslagTermijnPauzeSchema
>

export const gespreksverslagTermijnVerlengSchema = z.object({
  dagen: z.coerce.number().int().min(1, 'Minimaal 1 dag').max(180, 'Maximaal 180 dagen'),
  reden: z
    .string()
    .trim()
    .min(5, 'Geef een reden voor de verlenging (min. 5 tekens)')
    .max(1000),
})
export type GespreksverslagTermijnVerlengInput = z.infer<
  typeof gespreksverslagTermijnVerlengSchema
>

/** §3 - archiveren als expliciete eindstap (handmatige afsluiting). */
export const gespreksverslagArchiveerSchema = z.object({
  reden: z.string().trim().max(1000).optional(),
})
export type GespreksverslagArchiveerInput = z.infer<typeof gespreksverslagArchiveerSchema>

/** §12 - HR sluit een openstaande handtekening administratief af (uit dienst). */
export const gespreksverslagAdministratiefAfsluitenSchema = z.object({
  ondertekenaarId: z.string().trim().min(1, 'Kies de ondertekenaar'),
  reden: z
    .string()
    .trim()
    .min(5, 'Geef een reden voor de administratieve afsluiting (min. 5 tekens)')
    .max(1000),
})
export type GespreksverslagAdministratiefAfsluitenInput = z.infer<
  typeof gespreksverslagAdministratiefAfsluitenSchema
>

/** §3 - "Ter controle" aanbieden / goedkeuren / terugsturen. */
export const gespreksverslagTerControleSchema = z.object({
  controleurUserId: z.string().trim().min(1, 'Kies wie het verslag controleert'),
  opmerking: z.string().trim().max(2000).optional(),
})
export type GespreksverslagTerControleInput = z.infer<typeof gespreksverslagTerControleSchema>

export const gespreksverslagControleBesluitSchema = z
  .object({
    besluit: z.enum(['GOEDGEKEURD', 'TERUGGESTUURD']),
    opmerking: z.string().trim().max(2000).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.besluit === 'TERUGGESTUURD' && !v.opmerking) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['opmerking'],
        message: 'Geef aan wat er aangepast moet worden',
      })
    }
  })
export type GespreksverslagControleBesluitInput = z.infer<
  typeof gespreksverslagControleBesluitSchema
>

/**
 * §11a - datum, tijd of planner van een "Volgende afspraak"-regel bijwerken
 * NADAT het verslag definitief is.
 *
 * De verslaginhoud is dan bevroren, maar de planning van een vervolggesprek is
 * geen inhoud: "te plannen door X" wordt op enig moment "op 12-08 om 09:30".
 * Zonder dit pad zou je een volledige nieuwe verslagversie moeten starten om
 * een afspraak in te kunnen plannen.
 */
export const gespreksverslagVervolgPlanningSchema = z
  .object({
    datum: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een datum')
      .nullable()
      .optional(),
    tijd: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Gebruik een tijd als 09:30')
      .nullable()
      .optional(),
    tePlannenDoorEmployeeId: z.string().trim().min(1).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.tijd && v.datum === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tijd'],
        message: 'Een tijd hoort bij een datum: kies eerst een datum',
      })
    }
  })
export type GespreksverslagVervolgPlanningInput = z.infer<
  typeof gespreksverslagVervolgPlanningSchema
>

/** §5 - correctieblad/addendum bij een vastgelegde versie. */
export const gespreksverslagAddendumSchema = z.object({
  titel: z.string().trim().min(3, 'Geef het correctieblad een titel').max(200),
  tekst: z
    .string()
    .trim()
    .min(10, 'Vul de aanvulling in (min. 10 tekens)')
    .max(20000),
})
export type GespreksverslagAddendumInput = z.infer<typeof gespreksverslagAddendumSchema>

export interface GespreksverslagAddendumDTO {
  id: string
  versieNr: number
  titel: string
  tekst: string
  documentId: string | null
  aangemaaktDoorNaam: string | null
  createdAt: string
}

/** §6 - handmatig gekozen ondertekenaars (ondertekenplicht HANDMATIG). */
export const gespreksverslagOndertekenaarsSchema = z.object({
  ondertekenaars: z
    .array(
      z.object({
        employeeId: z.string().trim().min(1, 'Kies een medewerker'),
        rol: z.enum(GESPREKSVERSLAG_ONDERTEKENAAR_ROL),
      }),
    )
    .max(10, 'Maximaal 10 ondertekenaars'),
})
export type GespreksverslagOndertekenaarsInput = z.infer<
  typeof gespreksverslagOndertekenaarsSchema
>

/** §7 - termijninstellingen per gesprekstype (beheer). */
export const gespreksverslagTermijnConfigSchema = z
  .object({
    termijnHerinnering1Dagen: z.coerce.number().int().min(1).max(60),
    termijnHerinnering2Dagen: z.coerce.number().int().min(1).max(60),
    termijnEscalatieTlDagen: z.coerce.number().int().min(1).max(90),
    termijnEscalatieHrDagen: z.coerce.number().int().min(1).max(120),
    termijnVerlopenDagen: z.coerce.number().int().min(1).max(180),
  })
  .superRefine((v, ctx) => {
    const reeks: Array<[keyof typeof v, number]> = [
      ['termijnHerinnering1Dagen', v.termijnHerinnering1Dagen],
      ['termijnHerinnering2Dagen', v.termijnHerinnering2Dagen],
      ['termijnEscalatieTlDagen', v.termijnEscalatieTlDagen],
      ['termijnEscalatieHrDagen', v.termijnEscalatieHrDagen],
      ['termijnVerlopenDagen', v.termijnVerlopenDagen],
    ]
    for (let i = 1; i < reeks.length; i += 1) {
      if (reeks[i][1] <= reeks[i - 1][1]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [reeks[i][0]],
          message: 'Deze stap moet later liggen dan de vorige',
        })
      }
    }
  })
export type GespreksverslagTermijnConfigInput = z.infer<
  typeof gespreksverslagTermijnConfigSchema
>

// ─── Dashboard-DTO's (§8) ────────────────────────────────────────────────────

/** Eén regel in de widget "Laatste 10" en het volledige overzicht. */
export interface GespreksverslagOverzichtRowDTO {
  id: string
  gesprekId: string
  employeeId: string
  employeeNaam: string
  /** Neutraal label bij verzuim-redactie (nooit "Verzuimgesprek" naar buiten). */
  typeLabel: string
  /** yyyy-MM-dd (NL-kalenderdag) van het gesprek. */
  gespreksDatum: string | null
  leidinggevendeNaam: string | null
  hoofdstatus: GespreksverslagHoofdstatus
  actieStatus: GespreksverslagActieStatus
  /** ISO van het laatste tijdlijn-event. */
  laatsteWijzigingOp: string | null
  /** Effectief verstreken dagen sinds verzenden (pauzes eraf); null = niet verzonden. */
  dagenOpenstaand: number | null
  /** "2 van 3 getekend"; null als er geen tekenplicht is. */
  ondertekenStand: { getekend: number; vereist: number } | null
  /** ISO van de uiterste datum; null als de klok niet loopt. */
  deadlineOp: string | null
  deadlineOverschreden: boolean
  termijnGepauzeerd: boolean
  /** Aantal open actiepunten (los van de documentstatus, §11). */
  openActiepunten: number
  /** Verzuim-redactie actief voor deze lezer. */
  geredigeerd: boolean
}

/** Tellers boven het overzicht - server-side over de VOLLEDIGE set geteld. */
export interface GespreksverslagTellersDTO {
  concepten: number
  wachtOpOndertekening: number
  ongelezen: number
  verlopen: number
  geweigerd: number
  dezeMaandAfgerond: number
}

export const GESPREKSVERSLAG_SNELFILTERS = [
  'ALLE',
  'CONCEPTEN',
  'WACHT_ONDERTEKENING',
  'ONGELEZEN',
  'VERLOPEN',
  'GEWEIGERD',
  'DEZE_MAAND_AFGEROND',
  'OPEN_ACTIEPUNTEN',
] as const
export type GespreksverslagSnelfilter = (typeof GESPREKSVERSLAG_SNELFILTERS)[number]

export const GESPREKSVERSLAG_SNELFILTER_LABELS: Record<GespreksverslagSnelfilter, string> = {
  ALLE:                 'Alle verslagen',
  CONCEPTEN:            'Concepten',
  WACHT_ONDERTEKENING:  'Wacht op ondertekening',
  ONGELEZEN:            'Ongelezen',
  VERLOPEN:             'Verlopen',
  GEWEIGERD:            'Geweigerd',
  DEZE_MAAND_AFGEROND:  'Deze maand afgerond',
  OPEN_ACTIEPUNTEN:     'Open actiepunten',
}

/** Urgente melding bovenaan het dashboard (§8.2). */
export const GESPREKSVERSLAG_URGENTIE_SOORTEN = [
  'GEWEIGERD',
  'DEADLINE_OVERSCHREDEN',
  'TE_LANG_CONCEPT',
  'DEFINITIEF_NIET_VERZONDEN',
  'BEZWAAR',
  'NIEUWE_VERSIE_WACHT',
] as const
export type GespreksverslagUrgentieSoort = (typeof GESPREKSVERSLAG_URGENTIE_SOORTEN)[number]

export const GESPREKSVERSLAG_URGENTIE_LABELS: Record<GespreksverslagUrgentieSoort, string> = {
  GEWEIGERD:                 'Ondertekening geweigerd',
  DEADLINE_OVERSCHREDEN:     'Deadline overschreden',
  TE_LANG_CONCEPT:           'Te lang in concept',
  DEFINITIEF_NIET_VERZONDEN: 'Definitief maar niet verzonden',
  BEZWAAR:                   'Bezwaar toegevoegd',
  NIEUWE_VERSIE_WACHT:       'Nieuwe versie wacht op hertekening',
}

export interface GespreksverslagUrgentieDTO {
  soort: GespreksverslagUrgentieSoort
  aantal: number
  /** Snelfilter waar de doorklik heen gaat. */
  filter: GespreksverslagSnelfilter
}

/** Doorlooptijd-KPI's (§10) - gemiddelden in dagen over de gefilterde set. */
export interface GespreksverslagKpiDTO {
  /** Gemiddeld aantal dagen definitief -> gelezen. */
  gemiddeldDefinitiefNaarGelezen: number | null
  /** Gemiddeld aantal dagen definitief -> volledig ondertekend. */
  gemiddeldDefinitiefNaarGetekend: number | null
  aantalGeweigerd: number
  aantalVerlopen: number
  /** Verslagen met open actiepunten (geen opvolging afgerond). */
  aantalZonderOpvolging: number
  /** Medewerkers zonder gespreksverslag in de laatste 12 maanden. */
  medewerkersZonderRecentVerslag: number
}

export interface GespreksverslagDashboardDTO {
  laatste10: GespreksverslagOverzichtRowDTO[]
  tellers: GespreksverslagTellersDTO
  urgent: GespreksverslagUrgentieDTO[]
}

export interface GespreksverslagOverzichtResponseDTO {
  data: GespreksverslagOverzichtRowDTO[]
  meta: {
    total: number
    page: number
    pageSize: number
    tellers: GespreksverslagTellersDTO
    kpis: GespreksverslagKpiDTO
  }
}

// ─── Verloop-DTO op het verslag-detail (§4) ──────────────────────────────────

/**
 * Alles wat de statusbalk + tijdlijn + ondertekenaarslijst nodig heeft. Hangt
 * als `verloop` onder de bestaande GespreksverslagDTO zodat de bestaande
 * velden onaangeroerd blijven (regressie criterium 20).
 */
export interface GespreksverslagVerloopDTO {
  hoofdstatus: GespreksverslagHoofdstatus
  actieStatus: GespreksverslagActieStatus
  stappen: GespreksverslagStatusStapDTO[]
  tijdlijn: GespreksverslagEventDTO[]
  ondertekenaars: GespreksverslagOndertekenaarDTO[]
  ondertekenPlicht: GespreksverslagOndertekenPlicht
  terControleModus: GespreksverslagTerControleModus
  tekenverklaringOpties: GespreksverslagTekenverklaring[]
  termijn: GespreksverslagTermijnStand
  termijnConfig: GespreksverslagTermijnConfig
  addenda: GespreksverslagAddendumDTO[]
  /** Aantal open actiepunten (zichtbaar losgekoppeld van de documentstatus). */
  openActiepunten: number
  /** ISO of null - eigen FO-126-leesbevestiging van de medewerker. */
  leesbevestigingOp: string | null
  /** Verstrekkingskanaal van de laatste verzending. */
  laatsteKanaal: GespreksverslagVerstrekKanaal | null
}
